import { faClockRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isDefinedArray, reduce } from "et3-type-guards";

import i18n from "../../constants/i18n";
import ET3Div from "../../core/et3-div";
import { withLiteObserverAndTheme } from "../../core/et3-enhanced-render";
import Et3Text from "../../core/et3-text";
import Et3Tooltip from "../../core/et3-tooltip";
import getReactionHistoryStore from "../../stores/user-store/reaction-history-store";
import { Gender, TMessageReaction } from "../../Types";
import ReactionBadge from "./ReactionBadge";
import {
  HISTORY_ICON_SIZE,
  REACTION_BADGE_GAP,
  REACTION_BADGE_SIZE
} from "./reaction-constants";

const MAX_VISIBLE_REACTIONS = 2;

/**
 * A conversation has exactly two participants, so a message can carry at most
 * one reaction from the sender and one from the recipient. message_reactions
 * already holds a single row per reactor, this only guards the display.
 */
const getVisibleReactions = (
  reactions: TMessageReaction[],
  messageSenderId: number
) => {
  const latestBySenderId = new Map<number, TMessageReaction>();
  reactions.forEach((reaction) => {
    latestBySenderId.set(reaction.senderId, reaction);
  });
  const senderReaction = latestBySenderId.get(messageSenderId);
  const peerReactions = [...latestBySenderId.values()].filter(
    (reaction) => reaction.senderId !== messageSenderId
  );
  const ordered = senderReaction
    ? [senderReaction, ...peerReactions]
    : peerReactions;
  return ordered.slice(0, MAX_VISIBLE_REACTIONS);
};

/** "2026-08-19 10:48:21" -> "10:48:21", the format the ticket asks for inline. */
const getTimePart = (timestamp: string) => {
  const parts = reduce(() => timestamp.split(" "), [] as string[]);
  return parts.length > 1 ? parts[1] : reduce(() => timestamp, "");
};

const MessageReactions = withLiteObserverAndTheme<{
  messageId: number;
  messageSenderId: number;
  messageSenderGender: string;
  reactions: TMessageReaction[];
  hasReactionHistory: boolean;
  // Reports show the latest reaction time inline so an investigator does not
  // have to open the viewer for it.
  showLastReactionTime?: boolean;
  contentColor?: string;
}>((props) => {
  const {
    theme,
    messageId,
    messageSenderId,
    messageSenderGender,
    reactions,
    hasReactionHistory,
    showLastReactionTime = false,
    contentColor
  } = props;
  const { colors, unitX } = theme;
  const definedReactions = reduce(() => reactions, [] as TMessageReaction[]);
  const visibleReactions = isDefinedArray(definedReactions)
    ? getVisibleReactions(definedReactions, messageSenderId)
    : [];
  const iconColor = contentColor || colors.all.black;
  const lastReactionTime = visibleReactions
    .map((reaction) => reduce(() => reaction.reactedOn, ""))
    .sort()
    .pop();

  // No badges and no history means there is nothing to show, and the ticket
  // asks for no placeholder in that case.
  if (visibleReactions.length === 0 && !hasReactionHistory) {
    return null;
  }

  const getReactionGender = (reactionSenderId: number) => {
    if (reactionSenderId === messageSenderId) {
      return messageSenderGender;
    }
    return messageSenderGender === Gender.Male ? Gender.Female : Gender.Male;
  };

  return (
    <ET3Div
      isScrollable={false}
      isExtendable={true}
      withShadow={false}
      width={0}
      height={REACTION_BADGE_SIZE}
      fDirection="row"
      alignItems="center"
      justifyContent="flex-end"
      color={colors.all.transparent}
      style={{
        width: "100%",
        gap: REACTION_BADGE_GAP * unitX,
        paddingTop: REACTION_BADGE_GAP * unitX,
        paddingBottom: REACTION_BADGE_GAP * unitX
      }}
      onClick={() => {}}
    >
      {showLastReactionTime && lastReactionTime ? (
        <Et3Text
          fontFamily="BOLD"
          fontSize={11}
          text={getTimePart(lastReactionTime)}
          isSpan={true}
          color={iconColor}
          style={{}}
        />
      ) : null}
      {visibleReactions.map((reaction) => (
        <ReactionBadge
          key={`${messageId}-${reaction.senderId}`}
          reaction={reaction.reaction}
          gender={getReactionGender(reaction.senderId)}
          isSuppressed={reaction.isSuppressed}
        />
      ))}
      {hasReactionHistory ? (
        <Et3Tooltip title={i18n.get("REACTION_HISTORIES")} placement="top">
          <ET3Div
            isScrollable={false}
            isExtendable={false}
            withShadow={false}
            width={REACTION_BADGE_SIZE}
            height={REACTION_BADGE_SIZE}
            fDirection="row"
            alignItems="center"
            justifyContent="center"
            color={colors.all.transparent}
            style={{ cursor: "pointer", flexShrink: 0 }}
            onClick={() =>
              getReactionHistoryStore().openHistory({
                messageId,
                messageSenderId,
                messageSenderGender
              })
            }
          >
            <FontAwesomeIcon
              icon={faClockRotateLeft}
              color={iconColor}
              style={{ fontSize: HISTORY_ICON_SIZE * unitX }}
            />
          </ET3Div>
        </Et3Tooltip>
      ) : null}
    </ET3Div>
  );
});
export default MessageReactions;
